import React, { useState } from 'react';
import { Search, Globe, LogOut, Shield, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './Button';
import { IconButton } from './Button';
import { Badge } from './Badge';

export interface ReviseHeaderProps {
  user?: {
    name: string;
    email?: string;
    mobile: string;
    role: 'student' | 'admin' | 'super_admin';
    profile_picture?: string;
  } | null;
  language?: 'en' | 'hi';
  onLanguageToggle?: (lang: 'en' | 'hi') => void;
  onLoginClick?: () => void;
  onLogoutClick?: () => void;
  onProfileClick?: () => void;
  activeNavTab?: string;
  onNavTabChange?: (tab: string) => void;
}

export const ReviseHeader: React.FC<ReviseHeaderProps> = ({
  user,
  language = 'en',
  onLanguageToggle,
  onLoginClick,
  onLogoutClick,
  onProfileClick,
  activeNavTab = 'home',
  onNavTabChange,
}) => {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E5E9E9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Brand Logo & Title */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-[#E8FAF9] text-[#1fb4ac] flex items-center justify-center border border-[#56E8E1]/50 p-1.5 shadow-xs">
              <img src="/launch_icon.webp" alt="REVise" className="w-full h-full object-contain" />
            </div>
            <div>
              <span className="text-lg font-extrabold text-[#111111] tracking-tight">REVise</span>
              <span className="hidden sm:inline-block ml-2 px-2 py-0.5 text-[10px] font-bold text-[#1b8e88] bg-[#E8FAF9] rounded-md border border-[#56E8E1]/40 uppercase">
                Practice SaaS
              </span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-1">
            {[
              { id: 'home', label: 'Home' },
              { id: 'practice', label: 'Practice' },
              { id: 'revision', label: 'REVise' },
              { id: 'progress', label: 'Progress' },
              { id: 'account', label: 'Account' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => onNavTabChange?.(item.id)}
                className={`px-3.5 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  activeNavTab === item.id
                    ? 'bg-[#E8FAF9] text-[#1b8e88]'
                    : 'text-[#5F6368] hover:text-[#111111] hover:bg-[#F7FAFA]'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right Actions (Search, Language, User Profile) */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search Trigger */}
            <div className="hidden sm:flex items-center bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl px-3 py-1.5 text-xs text-[#5F6368] w-44 hover:border-[#CBD5E1] cursor-pointer">
              <Search className="w-3.5 h-3.5 mr-2 text-[#80868B]" />
              <span>Search question sets...</span>
            </div>

            {/* Language Switcher */}
            {onLanguageToggle && (
              <button
                onClick={() => onLanguageToggle(language === 'en' ? 'hi' : 'en')}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-[#111111] bg-[#F7FAFA] hover:bg-[#E8FAF9] hover:text-[#1b8e88] border border-[#E5E9E9] rounded-xl transition-all cursor-pointer"
                title="Toggle Language"
              >
                <Globe className="w-3.5 h-3.5 text-[#1fb4ac]" />
                <span>{language === 'en' ? 'EN' : 'HI'}</span>
              </button>
            )}

            {/* User Account / Auth */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-[#F7FAFA] transition-all cursor-pointer border border-transparent hover:border-[#E5E9E9]"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#1fb4ac] text-white font-extrabold text-xs flex items-center justify-center overflow-hidden">
                    {user.profile_picture ? (
                      <img src={user.profile_picture} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      user.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <span className="hidden lg:block text-xs font-bold text-[#111111] max-w-[100px] truncate">
                    {user.name}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-[#5F6368]" />
                </button>

                {/* Dropdown Menu */}
                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-[#E5E9E9] shadow-lg py-2 z-50 animate-in fade-in zoom-in-95 duration-150">
                    <div className="px-4 py-2.5 border-b border-[#E5E9E9]">
                      <p className="text-xs font-bold text-[#111111] truncate">{user.name}</p>
                      <p className="text-[11px] text-[#5F6368] truncate">{user.mobile}</p>
                      <Badge variant={user.role === 'admin' ? 'warning' : 'primary'} size="sm" className="mt-1.5">
                        {user.role}
                      </Badge>
                    </div>

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        onProfileClick?.();
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-medium text-[#111111] hover:bg-[#F7FAFA] flex items-center gap-2 cursor-pointer"
                    >
                      <Shield className="w-4 h-4 text-[#5F6368]" />
                      Account Settings
                    </button>

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        onLogoutClick?.();
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-medium text-[#D94A5C] hover:bg-[#FDEDEF] flex items-center gap-2 cursor-pointer border-t border-[#E5E9E9] mt-1"
                    >
                      <LogOut className="w-4 h-4 text-[#D94A5C]" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button variant="primary" size="sm" onClick={onLoginClick}>
                Sign In
              </Button>
            )}

            {/* Mobile Hamburger Menu Toggle */}
            <div className="md:hidden">
              <IconButton
                icon={mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                aria-label="Toggle mobile menu"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Header Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E5E9E9] py-3 px-2 space-y-1 animate-in slide-in-from-top-2 duration-150">
            {[
              { id: 'home', label: 'Home' },
              { id: 'practice', label: 'Practice' },
              { id: 'revision', label: 'REVise Flashcards' },
              { id: 'progress', label: 'Progress Analytics' },
              { id: 'account', label: 'Account Settings' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setMobileMenuOpen(false);
                  onNavTabChange?.(item.id);
                }}
                className={`w-full text-left px-4 py-2.5 text-xs font-bold rounded-xl transition-all ${
                  activeNavTab === item.id
                    ? 'bg-[#E8FAF9] text-[#1b8e88]'
                    : 'text-[#5F6368] hover:bg-[#F7FAFA] hover:text-[#111111]'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};
